import { getDataSource } from '@/lib/data-source';

// Types matching backend structure
export interface CurrencyConfigurationData {
  id?: string;
  currencyCode: string;
  currency_code?: string; // Backend field name
  displayName?: string;
  display_name?: string; // Backend field name
  symbol: string;
  symbolPosition: 'before' | 'after';
  symbol_position?: 'before' | 'after'; // Backend field name
  decimalPlaces: number;
  decimal_places?: number; // Backend field name
  thousandSeparator: string;
  thousand_separator?: string; // Backend field name
  decimalSeparator: string;
  decimal_separator?: string; // Backend field name
  roundingMode?: 'ceil' | 'floor' | 'round';
  rounding_mode?: 'ceil' | 'floor' | 'round'; // Backend field name
  exchangeRate: number;
  exchange_rate?: number; // Backend field name
  isPrimary: boolean;
  is_primary?: boolean; // Backend field name (response uses is_primary)
  isDefault?: boolean; // For compatibility
  is_default?: boolean; // Backend field name (request uses is_default)
  isActive: boolean;
  is_active?: boolean; // Backend field name
  showInSelector?: boolean;
  show_in_selector?: boolean; // Backend field name
  rateUpdatedAt?: string;
  rate_updated_at?: string; // Backend field name
  createdAt?: string;
  created_at?: string; // Backend field name
  updatedAt?: string;
  updated_at?: string; // Backend field name
}

export interface CurrencyConfigurationCreateData {
  currencyCode: string;
  symbol: string;
  symbolPosition?: 'before' | 'after';
  decimalPlaces?: number;
  thousandSeparator?: string;
  decimalSeparator?: string;
  exchangeRate: number;
  isPrimary?: boolean;
  isActive?: boolean;
}

export interface CurrencyConfigurationUpdateData extends Partial<CurrencyConfigurationCreateData> {}

export interface ExchangeRateUpdate {
  currencyCode: string;
  exchangeRate: number;
}

export interface CurrencyConfigurationListResponse {
  data: CurrencyConfigurationData[];
  meta?: {
    total: number;
    primaryCurrency: string;
  };
}

class CurrencyConfigurationService {
  private dataSource = getDataSource();

  /**
   * Get all currency configurations for a product
   */
  async getCurrencyConfigurations(productId: string): Promise<CurrencyConfigurationData[]> {
    try {
      const response = await this.dataSource.get<CurrencyConfigurationListResponse>(
        `/api/v1/products/${productId}/billing/currency-configurations`
      );
      
      console.log('Get currency configurations response:', response);
      console.log('Raw response.data:', JSON.stringify(response.data, null, 2));
      
      // Handle the response data - it might be JSON:API or already deserialized
      if (response.data && typeof response.data === 'object') {
        const responseData = response.data as unknown;
        console.log('ResponseData type check:', {
          isArray: Array.isArray(responseData),
          hasDataProperty: responseData && typeof responseData === 'object' && 'data' in responseData,
          firstItemCheck: Array.isArray(responseData) && responseData.length > 0 ? responseData[0] : null
        });
        
        // LiveAdapter deserializes JSON:API and wraps it in {data: Array}
        // So we need to check for the nested data structure
        if (typeof responseData === 'object' && responseData !== null && 
            'data' in responseData && Array.isArray((responseData as Record<string, unknown>).data)) {
          const dataArray = (responseData as Record<string, unknown>).data as unknown[];
          console.log('Found data array with length:', dataArray.length);
          console.log('First item in data array:', dataArray[0]);
          
          // The LiveAdapter should have already deserialized from JSON:API to plain objects
          // with camelCase keys
          return dataArray as CurrencyConfigurationData[];
        }
        
        // Fallback: Check if it's already an array of configurations
        if (Array.isArray(responseData)) {
          console.log('Direct array with length:', responseData.length);
          // Check if items are still in JSON:API format (shouldn't happen with LiveAdapter)
          if (responseData.length > 0 && responseData[0].attributes) {
            console.log('Still in JSON:API format, transforming manually');
            return responseData.map(item => ({
              id: item.id,
              ...item.attributes
            })) as CurrencyConfigurationData[];
          }
          return responseData as CurrencyConfigurationData[];
        }
      }
      
      return [];
    } catch (error: unknown) {
      // If it's a 404, return empty array (no configurations yet)
      if ((error as Record<string, unknown>)?.status === 404 || 
          ((error as Record<string, unknown>)?.response as Record<string, unknown>)?.status === 404) {
        console.log('No currency configurations found for product, returning empty array');
        return [];
      }
      
      // Don't throw authentication errors, let them be handled by the interceptor
      console.error(`Error fetching currency configurations for product ${productId}:`, error);
      
      // If it's a 401, don't re-throw (the interceptor will handle logout)
      if ((error as Record<string, unknown>)?.status === 401 || 
          ((error as Record<string, unknown>)?.response as Record<string, unknown>)?.status === 401) {
        return [];
      }
      
      throw error;
    }
  }

  /**
   * Get a specific currency configuration
   */
  async getCurrencyConfiguration(
    productId: string, 
    currencyCode: string
  ): Promise<CurrencyConfigurationData> {
    try {
      const response = await this.dataSource.get<{ data: CurrencyConfigurationData }>(
        `/api/v1/products/${productId}/billing/currency-configurations/${currencyCode}`
      );
      
      console.log('Get currency configuration response:', response);
      
      // Handle the response data
      if (response.data && typeof response.data === 'object') {
        const responseData = response.data as unknown as Record<string, unknown>;
        
        // If it has currencyCode, it's already the configuration object
        if (responseData.currencyCode) {
          return responseData as unknown as CurrencyConfigurationData;
        }
        
        // Otherwise check for data property
        if ('data' in responseData) {
          return responseData.data as CurrencyConfigurationData;
        }
      }
      
      throw new Error('Invalid response format');
    } catch (error) {
      console.error(`Error fetching currency configuration ${currencyCode}:`, error);
      throw error;
    }
  }

  /**
   * Add a new currency configuration
   */
  async createCurrencyConfiguration(
    productId: string,
    data: CurrencyConfigurationCreateData
  ): Promise<CurrencyConfigurationData> {
    try {
      // Prepare the request body in JSON:API format
      const requestBody = {
        data: {
          type: 'currency-configurations',
          attributes: {
            currency_code: data.currencyCode,
            exchange_rate: data.exchangeRate,
            decimal_places: data.decimalPlaces ?? 2,
            symbol: data.symbol,
            symbol_position: data.symbolPosition || 'before',
            thousand_separator: data.thousandSeparator || ',',
            decimal_separator: data.decimalSeparator || '.',
            is_default: data.isPrimary || false,
            is_active: data.isActive !== false
          }
        }
      };

      const response = await this.dataSource.post<{ data: CurrencyConfigurationData }>(
        `/api/v1/products/${productId}/billing/currency-configurations`,
        requestBody
      );
      
      console.log('Create currency configuration response:', response);
      
      // The LiveAdapter should have already deserialized the JSON:API response
      // Check if response.data contains the deserialized data
      if (response.data && typeof response.data === 'object') {
        const responseData = response.data as unknown;
        
        // If it has attributes, it's still in JSON:API format - extract them
        const responseObj = responseData as Record<string, unknown>;
        if ('attributes' in responseObj) {
          return {
            id: (responseObj as { id: string }).id,
            ...(responseObj.attributes as Record<string, unknown>)
          } as CurrencyConfigurationData;
        }
        
        // If it has currency_code or currencyCode, it's already deserialized
        if ('currency_code' in responseObj || 'currencyCode' in responseObj) {
          return responseObj as unknown as CurrencyConfigurationData;
        }
        
        // Check for nested data property
        if ('data' in responseObj) {
          const innerData = responseObj.data as Record<string, unknown>;
          if ('attributes' in innerData) {
            return {
              id: (innerData as { id: string }).id,
              ...(innerData.attributes as Record<string, unknown>)
            } as CurrencyConfigurationData;
          }
          return innerData as unknown as CurrencyConfigurationData;
        }
      }
      
      throw new Error('Invalid response format');
    } catch (error) {
      console.error(`Error creating currency configuration:`, error);
      throw error;
    }
  }

  /**
   * Update a currency configuration
   */
  async updateCurrencyConfiguration(
    productId: string,
    currencyCode: string,
    data: CurrencyConfigurationUpdateData
  ): Promise<CurrencyConfigurationData> {
    try {
      // Prepare the request body in JSON:API format
      const attributes: Record<string, unknown> = {};
      
      if (data.exchangeRate !== undefined) attributes.exchange_rate = data.exchangeRate;
      if (data.symbol !== undefined) attributes.symbol = data.symbol;
      if (data.symbolPosition !== undefined) attributes.symbol_position = data.symbolPosition;
      if (data.decimalPlaces !== undefined) attributes.decimal_places = data.decimalPlaces;
      if (data.thousandSeparator !== undefined) attributes.thousand_separator = data.thousandSeparator;
      if (data.decimalSeparator !== undefined) attributes.decimal_separator = data.decimalSeparator;
      if (data.isPrimary !== undefined) attributes.is_default = data.isPrimary;
      if (data.isActive !== undefined) attributes.is_active = data.isActive;

      const requestBody = {
        data: {
          type: 'currency-configurations',
          id: currencyCode,
          attributes
        }
      };

      const response = await this.dataSource.patch<{ data: CurrencyConfigurationData }>(
        `/api/v1/products/${productId}/billing/currency-configurations/${currencyCode}`,
        requestBody
      );
      
      console.log('Update currency configuration response:', response);
      
      // Handle the response data
      if (response.data && typeof response.data === 'object') {
        const responseData = response.data as unknown as Record<string, unknown>;
        if (responseData.currencyCode || responseData.currency_code) {
          return responseData as unknown as CurrencyConfigurationData;
        }
        if ('data' in responseData) {
          return responseData.data as CurrencyConfigurationData;
        }
      }
      
      throw new Error('Invalid response format');
    } catch (error) {
      console.error(`Error updating currency configuration ${currencyCode}:`, error);
      throw error;
    }
  }

  /**
   * Delete a currency configuration
   */
  async deleteCurrencyConfiguration(productId: string, currencyCode: string): Promise<void> {
    try {
      await this.dataSource.delete(
        `/api/v1/products/${productId}/billing/currency-configurations/${currencyCode}`
      );
    } catch (error) {
      console.error(`Error deleting currency configuration ${currencyCode}:`, error);
      throw error;
    }
  }

  /**
   * Bulk update exchange rates
   */
  async updateExchangeRates(
    productId: string,
    rates: ExchangeRateUpdate[]
  ): Promise<CurrencyConfigurationData[]> {
    try {
      // Prepare the request body in JSON:API format
      const requestBody = {
        data: {
          type: 'exchange-rate-updates',
          attributes: {
            rates: rates.map(rate => ({
              currency_code: rate.currencyCode,
              exchange_rate: rate.exchangeRate
            }))
          }
        }
      };

      const response = await this.dataSource.patch<{ data: CurrencyConfigurationData[] }>(
        `/api/v1/products/${productId}/billing/currency-configurations/exchange-rates`,
        requestBody
      );
      
      console.log('Update exchange rates response:', response);
      
      // Handle the response data
      if (response.data && typeof response.data === 'object') {
        const responseData = response.data as unknown as Record<string, unknown>;
        if (Array.isArray(responseData)) {
          return responseData as CurrencyConfigurationData[];
        }
        if ('data' in responseData && Array.isArray(responseData.data)) {
          return responseData.data as CurrencyConfigurationData[];
        }
      }
      
      return [];
    } catch (error) {
      console.error(`Error updating exchange rates:`, error);
      throw error;
    }
  }

  /**
   * Set primary currency
   */
  async setPrimaryCurrency(productId: string, currencyCode: string): Promise<CurrencyConfigurationData> {
    return this.updateCurrencyConfiguration(productId, currencyCode, { isPrimary: true });
  }

  /**
   * Toggle currency active status
   */
  async toggleCurrencyStatus(productId: string, currencyCode: string, isActive: boolean): Promise<CurrencyConfigurationData> {
    return this.updateCurrencyConfiguration(productId, currencyCode, { isActive });
  }
}

export const currencyConfigurationService = new CurrencyConfigurationService();